import React, { useState, useEffect } from 'react';
import { Typography, Box, Button, Modal, CircularProgress, InputAdornment } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTheme } from '../../contexts/ThemeContext';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import CustomInput from '../Inputs/CustomInput';
import ErrorModal from './ErrorModal';

const SafetyDataSheetsModal = ({ open, setOpen, options, onSelect, handleAddSafetySheet }) => {
  const { theme } = useTheme();

  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorModal, setopenErrorModal] = useState(false);
  const [previewLink, setPreviewLink] = useState(null);
  const [safetyDataSheet, setSafetyDataSheet] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [activePreviewIndex, setActivePreviewIndex] = useState(null);

  useEffect(() => {
    if (!open) {
      setPreviewLink(null);
      setIsLoading(false);
      setPreviewError(false);
      setActivePreviewIndex(null);
    }
  }, [open]);

  const alertError = (msg) => {
    setErrorMessage(msg);
    setopenErrorModal(true);
  };

  const handleLinkClick = (item) => {
    onSelect(item);
    setOpen(false);
  };

  const buildLink = (item) => {
    return `http://msds.walmartstores.com/client/document?productid=${item.ProductID}&productguid=${item.ProductGuid}&DocumentKey=undefined&HazdocumentKey=undefined&MSDS=0&subformat=NAM`;
  };

  const handlePreview = (link, index) => {
    if (index === activePreviewIndex) {
      setPreviewLink(null);
      setActivePreviewIndex(null);
      return;
    }
    setIsLoading(true);
    setPreviewError(false);
    setPreviewLink(link);
    setActivePreviewIndex(index);
  };

  const handleOpenInNewTab = (link, event) => {
    event.stopPropagation();
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setPreviewError(true);
  };

  const handleAdd = () => {
    if (!safetyDataSheet) {
      alertError("Please enter a valid URL format.");
      return;
    }
    if (safetyDataSheet.length > 2500) {
      alertError("The Safety Data Sheet URL has a maximum limit of 2500 characters.");
      return;
    }
    setSafetyDataSheet("");
    handleAddSafetySheet(safetyDataSheet)
  };

  const styles = {
    modal: {
      position: 'absolute',
      top: '53%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '80vw',
      maxWidth: '1000px',
      maxHeight: '80vh',
      // overflow: 'auto',
      bgcolor: theme.background.primary,
      borderRadius: 2,
      border: `4px solid ${theme.color.primary}`,
      boxShadow: 24,
      p: 4,
      outline: 'none',
    },
    title: {
      fontFamily: theme.fontFamily,
      textAlign: 'center',
      color: theme.text.primary,
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${theme.border.light}`,
      backgroundColor: theme.background.secondary,
      padding: 2,
      borderRadius: 1,
      cursor: 'pointer'
    },
    cardInfo: {
      fontSize: '0.75rem',
      textAlign: 'center',
      color: theme.text.primary,
      fontFamily: theme.fontFamily,
      mt: 1
    },
    previewContainer: {
      mt: 3,
      width: '100%',
      height: '400px',
      border: `1px solid ${theme.border.light}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      position: 'relative'
    },
    closeBtn: {
      position: 'absolute',
      top: 8,
      right: 8,
      minWidth: 'unset',
      width: 36,
      height: 36,
      borderRadius: '50%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: 'white',
      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
    }
  }

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={styles.modal}>
          <Typography variant="h6" gutterBottom sx={styles.title}>
            Select a Safety Data Sheet
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3, justifyContent: 'center' }}>
            {Array.isArray(options) && options.map((item, index) => {
              const link = buildLink(item);
              const isActive = index === activePreviewIndex;

              return (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Box sx={styles.card} onClick={() => handleLinkClick(item)}>
                    <DescriptionIcon sx={{ fontSize: '2rem', color: theme.color.primary }} />
                    <Typography sx={styles.cardInfo}>
                      Product ID: {item.ProductID}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreview(`https://corsproxy.io/?${encodeURIComponent(link)}`, index);
                      }}
                      sx={{ fontFamily: theme.fontFamily, fontSize: '0.75rem', ...(isActive ? { backgroundColor: theme.color.primary, color: theme.text.negative } : {}) }}
                    >
                      {isActive ? "Close" : "Preview"}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      fullWidth
                      onClick={(e) => handleOpenInNewTab(link, e)}
                      sx={{ fontFamily: theme.fontFamily, fontSize: '0.75rem' }}
                    >
                      Open <OpenInNewIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                    </Button>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {previewLink && (
            <Box sx={styles.previewContainer}>
              <Button
                sx={styles.closeBtn}
                onClick={() => handlePreview(null, null)}
              >
                <CloseIcon />
              </Button>
              {isLoading && <CircularProgress />}
              {previewError ? (
                <Typography sx={{ textAlign: 'center', color: theme.accent.error }}>
                  Unable to display preview. Open in new tab instead.
                </Typography>
              ) : (
                <iframe
                  src={previewLink}
                  title="MSDS Preview"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                />
              )}
            </Box>
          )}

          <Grid container>
            <Grid item size={6} offset={3}>
              <CustomInput
                fullWidth
                size="small"
                label={"Safety Data Sheet"}
                value={safetyDataSheet}
                onChange={(e) => setSafetyDataSheet(e.target.value)}
                type="text"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ color: theme.color.primary, cursor: 'pointer', fontFamily: theme.fontFamily }} onClick={handleAdd}>Add</Typography>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-evenly', mt: 2 }}>
            <Button variant="contained" onClick={handleAdd} sx={{
              textTransform: 'none',
              fontFamily: theme.fontFamily,
              color: theme.text.negative,
              borderRadius: '20px',
              backgroundColor: theme.accent.success,
              border: `1px solid ${theme.border.light}`
            }}>
              Submit
            </Button>
            <Button variant="contained" onClick={() => setOpen(false)} sx={{
              textTransform: 'none',
              fontFamily: theme.fontFamily,
              color: theme.text.negative,
              borderRadius: '20px',
              backgroundColor: theme.accent.error,
              border: `1px solid ${theme.border.light}`
            }}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      <ErrorModal open={openErrorModal} setOpen={setopenErrorModal} message={errorMessage} />
    </>
  );
};

export default SafetyDataSheetsModal;
